import { useState, useEffect } from 'react';
import { useUiContextStore } from "../../stores/uiContextStore";
import { useNavigate, useParams } from 'react-router';
import {
  ArrowLeft, ChevronRight, Calendar, FileText,
  Plus, Trash2, Pencil, CheckCheck, Zap,
} from 'lucide-react';
import { useToastStore } from "../../stores/toastStore";
import { RBACAction } from '../components/RBACButton';
import { Dropdown } from '../components/Dropdown';
import { Modal, FormField, FormInput, FormSelect, FormTextarea, FormDatePicker, ModalFooterButtons } from '../components/Modal';
import {
  Tip,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import {
  getPoam, getRemediation, updateRemediation,
  searchPoamItems,
  transitionPoamItemStatus,
  updatePoamItemSeverity,
  parseMilestones, serializeMilestones,
  listUsers,
  ApiPoamItem, ApiMilestone, ApiUser, RemediationStatusApi,
  type PoamSeverity,
} from '../../api/frameworksApi';
import { validateRequired, validateDate } from '../../utils/validators';
import { todayISO } from '../../utils/formatters';

/* ─── Board meta helpers (shared with POAMDetail) ─── */
type BoardStatus = 'open' | 'in_progress' | 'progress_hold' | 'accepted_risk' | 'verification' | 'closed_completed' | 'completed';

interface ItemBoardMeta {
  status: BoardStatus;
  assignee: string;
  holdReason?: string;
  holdDate?: string;
}

function boardMetaKey(poamId: string): string {
  return `poam_board_meta_${poamId}`;
}

const ASSIGNEE_PAGE_SIZE = 200;
const ASSIGNEE_MAX_PAGES = 50;


function loadBoardMeta(poamId: string): Record<string, ItemBoardMeta> {
  try {
    const raw = localStorage.getItem(boardMetaKey(poamId));
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

function saveBoardMeta(poamId: string, meta: Record<string, ItemBoardMeta>) {
  localStorage.setItem(boardMetaKey(poamId), JSON.stringify(meta));
}

function getItemMeta(meta: Record<string, ItemBoardMeta>, itemId: string): ItemBoardMeta {
  return meta[itemId] ?? { status: 'open', assignee: '' };
}

function updateItemMeta(poamId: string, itemId: string, patch: Partial<ItemBoardMeta>) {
  const all = loadBoardMeta(poamId);
  all[itemId] = { ...getItemMeta(all, itemId), ...patch };
  saveBoardMeta(poamId, all);
}

/* ─── Map board status to remediation API status ─── */
function boardToRemediationStatus(bs: BoardStatus): RemediationStatusApi {
  switch (bs) {
    case 'open': return 'open';
    case 'in_progress': return 'in_progress';
    case 'progress_hold': return 'delayed';
    case 'accepted_risk': return 'in_progress';
    case 'verification': return 'in_progress';
    case 'closed_completed': return 'completed';
    case 'completed': return 'completed';
  }
}

function itemApiStatusToBoardStatus(apiStatus: string | undefined): BoardStatus {
  switch (apiStatus) {
    case 'identified_finding': return 'open';
    case 'remediation_in_progress': return 'in_progress';
    case 'remediation_on_hold': return 'progress_hold';
    case 'accepted_risk': return 'accepted_risk';
    case 'validation_and_testing': return 'verification';
    case 'completed': return 'closed_completed';
    case 'closed_remediated': return 'completed';
    case 'remediated': return 'completed';
    default: return 'open';
  }
}

/* ─── DD/MM/YYYY masked date input — stores ISO yyyy-mm-dd ─── */
function isoToDisplay(iso: string): string {
  if (!iso) return '';
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
}
function displayToIso(display: string): string {
  const m = display.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return '';
  const dd = parseInt(m[1], 10), mm = parseInt(m[2], 10);
  if (mm < 1 || mm > 12 || dd < 1 || dd > 31) return '';
  return `${m[3]}-${m[2]}-${m[1]}`;
}
function DateInputDDMMYYYY({ value, onChange, disabled }: { value: string; onChange: (iso: string) => void; disabled?: boolean }) {
  const [text, setText] = useState(isoToDisplay(value));
  useEffect(() => { setText(isoToDisplay(value)); }, [value]);
  const handleChange = (raw: string) => {
    const digits = raw.replace(/\D/g, '').slice(0, 8);
    let out = digits;
    if (digits.length > 4) out = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
    else if (digits.length > 2) out = `${digits.slice(0, 2)}/${digits.slice(2)}`;
    setText(out);
    onChange(out.length === 10 ? displayToIso(out) : '');
  };
  return (
    <input
      type="text"
      inputMode="numeric"
      placeholder="DD/MM/YYYY"
      value={text}
      onChange={(e) => handleChange(e.target.value)}
      disabled={disabled}
      className="w-full text-sm bg-[#f5f7fb] dark:bg-white/[0.04] rounded-lg px-3 py-2 outline-none border border-[#e2e8f0] dark:border-white/[0.07] focus:border-indigo-500 dark:focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10 dark:focus:ring-indigo-500/10 transition-all placeholder:text-[#64748b] dark:placeholder:text-white/25 text-[#0f172a] dark:text-white disabled:opacity-50"
    />
  );
}

/* ─── Status badge ─── */
const STATUS_STYLES: Record<BoardStatus, { chip: string; dot: string; label: string }> = {
  open:             { chip: 'bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-400 ring-1 ring-red-200 dark:ring-red-500/20',                        dot: 'bg-red-500',     label: 'Identified Finding' },
  in_progress:      { chip: 'bg-sky-50 dark:bg-sky-500/10 text-sky-700 dark:text-sky-400 ring-1 ring-sky-200 dark:ring-sky-500/20',                        dot: 'bg-sky-500',     label: 'Remediation In Progress' },
  progress_hold:    { chip: 'bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 ring-1 ring-amber-200 dark:ring-amber-500/20',             dot: 'bg-amber-500',   label: 'Remediation On Hold' },
  accepted_risk:    { chip: 'bg-orange-50 dark:bg-orange-500/10 text-orange-700 dark:text-orange-400 ring-1 ring-orange-200 dark:ring-orange-500/20',       dot: 'bg-orange-500',  label: 'Accepted Risk' },
  verification:     { chip: 'bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-400 ring-1 ring-blue-200 dark:ring-blue-500/20',                   dot: 'bg-blue-500',    label: 'Validation & Testing' },
  closed_completed: { chip: 'bg-teal-50 dark:bg-teal-500/10 text-teal-700 dark:text-teal-400 ring-1 ring-teal-200 dark:ring-teal-500/20',                   dot: 'bg-teal-500',    label: 'Completed' },
  completed:        { chip: 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 ring-1 ring-emerald-200 dark:ring-emerald-500/20', dot: 'bg-emerald-500', label: 'Remediated' },
};

function ItemStatusBadge({ status }: { status: BoardStatus }) {
  const v = STATUS_STYLES[status] ?? STATUS_STYLES.open;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold ${v.chip}`}>
      <span className={`size-1.5 rounded-full flex-shrink-0 ${v.dot}`} />
      {v.label}
    </span>
  );
}

function Skeleton({ className = '' }: { className?: string }) {
  return <div className={`bg-[#f1f5f9] dark:bg-white/[0.05] rounded-full animate-pulse ${className}`} />;
}

/* ═══════════════════════════════════════════════════════════════
   Main Component
   ═══════════════════════════════════════════════════════════════ */
const ITEM_SEVERITY_META: Record<PoamSeverity, { label: string; dot: string; badge: string }> = {
  critical: { label: "Critical", dot: "bg-red-500", badge: "bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-400 border-red-300 dark:border-red-500/30" },
  high: { label: "High", dot: "bg-orange-500", badge: "bg-orange-50 dark:bg-orange-500/10 text-orange-700 dark:text-orange-400 border-orange-300 dark:border-orange-500/30" },
  medium: { label: "Medium", dot: "bg-yellow-500", badge: "bg-yellow-50 dark:bg-yellow-500/10 text-yellow-700 dark:text-yellow-500 border-yellow-300 dark:border-yellow-500/30" },
  low: { label: "Low", dot: "bg-slate-400", badge: "bg-slate-100 dark:bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-300 dark:border-slate-500/30" },
};

export function POAMItemDetail() {
  const navigate = useNavigate();
  const { id: poamId, itemId } = useParams<{ id: string; itemId: string }>();

  const [poamTitle, setPoamTitle] = useState('');
  const [item, setItem] = useState<ApiPoamItem | null>(null);
  // Publish the open item with the POA&M it sits in, so a question can name both — "explain this
  // finding" is ambiguous across POA&Ms in a way the parent resolves. Cleared on unmount.
  useEffect(() => {
    const title = item?.title;
    useUiContextStore
      .getState()
      .setSelectedItem(
        itemId && title
          ? { id: itemId, name: title, parentName: poamTitle || undefined }
          : null,
      );
    return () => useUiContextStore.getState().clearSelectedItem();
  }, [itemId, item, poamTitle]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  /* Remediation state */
  const [planTitle, setPlanTitle] = useState('');
  const [planDesc, setPlanDesc] = useState('');
  const [planDate, setPlanDate] = useState('');
  const [assignee, setAssignee] = useState('');
  const [users, setUsers] = useState<ApiUser[]>([]);
  const [milestones, setMilestones] = useState<ApiMilestone[]>([]);
  const [boardStatus, setBoardStatus] = useState<BoardStatus>('open');
  const [actualCompletionDate, setActualCompletionDate] = useState<string | null>(null);

  /* Track if plan was already saved (came from cache/API) */
  const [planSaved, setPlanSaved] = useState(false);

  /* Severity assignment */
  const [savingSeverity, setSavingSeverity] = useState(false);
  const handleSetSeverity = async (severity: PoamSeverity) => {
    if (!poamId || !itemId) return;
    setSavingSeverity(true);
    try {
      const res = await updatePoamItemSeverity(poamId, itemId, severity);
      setItem((prev) => (prev ? { ...prev, severity: res.data.severity } : prev));
    } catch (e: any) {
      const apiMsg = e?.responseBody?.errors?.[0]?.detail || e?.responseBody?.errors?.[0]?.title || e?.message;
      useToastStore.getState().showError("Error", apiMsg || "Failed to update severity");
    } finally {
      setSavingSeverity(false);
    }
  };

  /* Expanded asset rows (command + evidence) in the automated finding detail */
  const [expandedAssets, setExpandedAssets] = useState<Set<string>>(new Set());
  const toggleAsset = (uuid: string) =>
    setExpandedAssets((prev) => {
      const next = new Set(prev);
      if (next.has(uuid)) next.delete(uuid);
      else next.add(uuid);
      return next;
    });

  /* Add milestone dialog */
  const [showAddMs, setShowAddMs] = useState(false);
  const [msTitle, setMsTitle] = useState('');

  /* Edit milestone dialog */
  const [showEditMs, setShowEditMs] = useState(false);
  const [editMsId, setEditMsId] = useState('');
  const [editMsTitle, setEditMsTitle] = useState('');

  /* Confirmation dialogs */
  const [showStartDialog, setShowStartDialog] = useState(false);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [poamRes, remRes, usersRes] = await Promise.all([
        getPoam(poamId!),
        getRemediation(poamId!, itemId!),
        listUsers(),
      ]);

      /* Guard API response envelopes before accessing nested fields */
      if (!poamRes?.data) throw new Error('Invalid POA&M response envelope');
      if (!Array.isArray(poamRes.data.items)) {
        console.warn('POA&M items missing or not an array, defaulting to empty');
        poamRes.data.items = [];
      }

      /* Normalize collections at point of state set to prevent undefined.map/length downstream */
      const usersList = Array.isArray(usersRes?.data) ? usersRes.data : [];
      setUsers(usersList);

      const poamItems = poamRes.data.items ?? [];
      const foundItem = poamItems.find(i => i?.id === itemId) ?? null;

      /* If item not found, provide clear error instead of blank page */
      if (!foundItem) {
        throw new Error(`Item ${itemId} not found in POA&M. Data may have been deleted.`);
      }

      setPoamTitle(poamRes.data.title ?? `POA&M ${poamId!.slice(-8)}`);
      setItem(foundItem);

      /* Defensive: Ensure assigneeNames is array; guard each element */
      const assigneeNames = Array.isArray(foundItem.assigneeNames)
        ? foundItem.assigneeNames.filter(n => n && typeof n === 'string' && n.trim())
        : [];
      const apiAssignee = assigneeNames[0] || '';
      setAssignee(apiAssignee);

      const apiDerivedStatus = itemApiStatusToBoardStatus(foundItem.status);
      setBoardStatus(apiDerivedStatus);
      updateItemMeta(poamId!, itemId!, { status: apiDerivedStatus });

      /* Optional: Parse remediation data only if response envelope is valid */
      if (remRes?.data) {
        const rem = remRes.data;
        setPlanTitle((rem.planTitle ?? '').trim());
        setPlanDesc((rem.planDescription ?? '').trim());
        if (rem.scheduledCompletionDate && typeof rem.scheduledCompletionDate === 'string') {
          setPlanDate(rem.scheduledCompletionDate.split('T')[0]);
        }
        const parsedMs = Array.isArray(rem.milestones) ? parseMilestones(rem.milestones) : [];
        setMilestones(parsedMs);
        setActualCompletionDate(rem.actualCompletionDate ?? null);
        setPlanSaved(true);
      }
    } catch (e: any) {
      const errorMsg = e?.message ?? 'Failed to load item details';
      setError(errorMsg);
      console.error('POAMItemDetail fetch error:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (poamId && itemId) fetchData();
  }, [poamId, itemId]);

  useEffect(() => { if (error) useToastStore.getState().showError("Error", error); }, [error]);

  const hasPlan = !!(planTitle && planDesc && planDate && assignee);
  const totalMilestones = milestones.length;
  const completedMilestones = milestones.filter(m => m.completed).length;
  const allMilestonesComplete = totalMilestones > 0 && completedMilestones === totalMilestones;
  const canStart = boardStatus === 'open' && hasPlan && totalMilestones > 0;

  /* Is the plan form editable? Only when open and not yet saved */
  const planEditable = boardStatus === 'open';
  const showSaveButton = planEditable && !planSaved;

  /* Convert "YYYY-MM-DD" to "YYYY-MM-DDT00:00:00Z" for backend Instant fields */
  const toInstant = (d: string | null | undefined): string | null =>
    d ? (d.includes('T') ? d : `${d}T00:00:00Z`) : null;

  const remediationStatus = boardToRemediationStatus(boardStatus);

  /* Resolve the selected assignee (display string) back to its user id */
  const assigneeUserId = users.find(
    (u) => (u.displayName || `${u.givenName} ${u.familyName}`.trim() || u.email) === assignee,
  )?.id;

  /* Build full remediation payload */
  const buildPayload = (overrides: Partial<Parameters<typeof updateRemediation>[2]> = {}) => {
    const merged = {
      planTitle: planTitle.trim() || '',
      planDescription: planDesc.trim() || '',
      milestones: serializeMilestones(milestones),
      scheduledCompletionDate: toInstant(planDate),
      actualCompletionDate: toInstant(actualCompletionDate),
      status: remediationStatus,
      assigneeUserIds: assigneeUserId ? [assigneeUserId] : [],
      ...overrides,
    };
    if (merged.scheduledCompletionDate) merged.scheduledCompletionDate = toInstant(merged.scheduledCompletionDate);
    if (merged.actualCompletionDate) merged.actualCompletionDate = toInstant(merged.actualCompletionDate);
    return merged;
  };

  const callUpdate = async (payload: Parameters<typeof updateRemediation>[2]): Promise<boolean> => {
    setSaving(true);
    try {
      await updateRemediation(poamId!, itemId!, payload);
      return true;
    } catch (e: any) {
      const apiErr = e?.responseBody?.errors?.[0];
      /* Prefix the offending field so "must not be blank" is actionable */
      const field = apiErr?.pointer ? apiErr.pointer.replace(/^\//, '') : '';
      const detail = apiErr?.detail || apiErr?.title || e?.message || 'Failed to save';
      /* Save failures are shown as a toast — the page-level banner offers a
         Retry that refetches, which is only meaningful for load failures. */
      useToastStore.getState().showError('Could not save', field ? `${field}: ${detail}` : detail);
      return false;
    } finally {
      setSaving(false);
    }
  };

  /* Sync board meta status + assignee whenever they change */
  const syncBoardMeta = (newStatus?: BoardStatus, newAssignee?: string) => {
    const patch: Partial<ItemBoardMeta> = {};
    if (newStatus !== undefined) patch.status = newStatus;
    if (newAssignee !== undefined) patch.assignee = newAssignee;
    updateItemMeta(poamId!, itemId!, patch);
  };

  const handleSavePlan = async () => {
    // Validate title
    const titleVal = validateRequired(planTitle, "Title");
    if (!titleVal.valid) {
      setError("Title is required");
      return;
    }

    // Validate description
    const descVal = validateRequired(planDesc, "Description");
    if (!descVal.valid) {
      setError("Description is required");
      return;
    }

    // Validate date
    const dateVal = validateRequired(planDate, "Scheduled completion date");
    if (!dateVal.valid) {
      setError("Scheduled completion date is required");
      return;
    }

    // Validate assignee
    const assigneeVal = validateRequired(assignee, "Assignee");
    if (!assigneeVal.valid) {
      setError("Assignee is required");
      return;
    }

    // Validate date format and reasonable range
    const displayDate = isoToDisplay(planDate);
    const dateFormatVal = validateDate(displayDate, "DD/MM/YYYY");
    if (!dateFormatVal.valid) {
      setError(dateFormatVal.error || "Invalid date format");
      return;
    }

    // Check if date is in a reasonable range (not year 1900 or earlier)
    const [dd, mm, yyyy] = displayDate.split("/").map(Number);
    if (yyyy < 1900) {
      setError("Date year must be 1900 or later");
      return;
    }

    if (planDate < todayISO()) {
      setError("Scheduled completion cannot be in the past");
      return;
    }

    const ok = await callUpdate(buildPayload({ planTitle: planTitle.trim(), planDescription: planDesc.trim(), scheduledCompletionDate: toInstant(planDate), status: 'open' }));
    if (!ok) return;
    syncBoardMeta(undefined, assignee.trim());
    localStorage.setItem(`poam_has_plan_${poamId}_${itemId}`, 'true');
    setPlanSaved(true);
    navigate(`/continuous-compliance/risk/poam/${poamId}`);
  };

  const handleAssigneeBlur = () => {
    syncBoardMeta(undefined, assignee.trim());
  };

  /* Milestone changes reuse the full remediation payload, and the backend rejects
     it with "must not be blank" on /planTitle when the plan is still empty. Tell
     the user what is missing instead of firing a PUT that can only fail. */
  const requirePlanForMilestones = (): boolean => {
    const missing: string[] = [];
    if (!planTitle.trim()) missing.push('Plan Title');
    if (!planDesc.trim()) missing.push('Description');
    if (!assignee.trim()) missing.push('Assignee');
    if (!planDate) missing.push('Scheduled Completion');
    if (missing.length === 0) return true;
    useToastStore.getState().showWarning(
      'Remediation plan incomplete',
      `Fill in ${missing.join(', ')} in the remediation plan before adding milestones.`,
    );
    return false;
  };

  const handleOpenAddMilestone = () => {
    if (!requirePlanForMilestones()) return;
    setShowAddMs(true);
  };

  const handleAddMilestone = async () => {
    if (!msTitle.trim()) return;
    if (!requirePlanForMilestones()) return;
    const newMs: ApiMilestone = { id: `ms-${Date.now()}`, name: msTitle.trim(), completed: false };
    const updated = [...milestones, newMs];
    const ok = await callUpdate(buildPayload({ milestones: serializeMilestones(updated) }));
    if (!ok) return;
    setMilestones(updated);
    setShowAddMs(false);
    setMsTitle('');
  };

  const handleDeleteMilestone = async (msId: string) => {
    if (boardStatus !== 'open') return;
    const updated = milestones.filter(m => m.id !== msId);
    const ok = await callUpdate(buildPayload({ milestones: serializeMilestones(updated) }));
    if (!ok) return;
    setMilestones(updated);
  };

  const handleEditMilestone = async () => {
    if (!editMsTitle.trim()) return;
    const updated = milestones.map(m =>
      (m.id ?? m.name) === editMsId ? { ...m, name: editMsTitle.trim() } : m
    );
    const ok = await callUpdate(buildPayload({ milestones: serializeMilestones(updated) }));
    if (!ok) return;
    setMilestones(updated);
    setShowEditMs(false);
    setEditMsId('');
    setEditMsTitle('');
  };

  const handleToggleMilestone = async (msId: string) => {
    if (boardStatus !== 'in_progress') return;
    const updated = milestones.map(m => m.id === msId ? { ...m, completed: !m.completed } : m);
    const ok = await callUpdate(buildPayload({ milestones: serializeMilestones(updated), status: 'in_progress' }));
    if (!ok) return;
    setMilestones(updated);
  };

  const handleStartRemediation = async () => {
    const ok = await callUpdate(buildPayload({ status: 'in_progress' }));
    if (!ok) return;
    setBoardStatus('in_progress');
    syncBoardMeta('in_progress');
    setShowStartDialog(false);
  };

  const handleMoveToCompleted = async () => {
    try {
      await transitionPoamItemStatus(poamId!, itemId!, { targetStatus: 'validation_and_testing' });
      setShowCompleteDialog(false);
      setBoardStatus('verification');
      syncBoardMeta('verification');
      navigate(`/continuous-compliance/risk/poam/${poamId}`);
    } catch (e: any) {
      const apiMsg = e?.responseBody?.errors?.[0]?.detail || e?.responseBody?.errors?.[0]?.title || e?.message;
      setError(apiMsg ?? 'Failed to move item to Validation & Testing');
    }
  };

  /* When plan fields change, mark as unsaved */
  const handlePlanFieldChange = (setter: (v: string) => void) => (v: string) => {
    setter(v);
    setPlanSaved(false);
  };

  /* ── Loading state ── */
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-2">
          <Skeleton className="h-4 w-24" />
          <ChevronRight className="size-3.5 text-muted-foreground" />
          <Skeleton className="h-4 w-32" />
          <ChevronRight className="size-3.5 text-muted-foreground" />
          <Skeleton className="h-4 w-40" />
        </div>
        <div className="flex items-start gap-3">
          <Skeleton className="size-8 rounded-lg" />
          <div className="space-y-2 flex-1">
            <Skeleton className="h-7 w-64" />
            <Skeleton className="h-4 w-40" />
          </div>
        </div>
        <div className="rounded-xl border border-[#e2e8f0] dark:border-white/[0.06] bg-white dark:bg-[#131320] p-5 space-y-4">
          <Skeleton className="h-5 w-24" />
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {[0, 1, 2].map(i => <Skeleton key={i} className="h-10 w-full rounded-lg" />)}
          </div>
        </div>
      </div>
    );
  }

  /* ── Error / not found ── */
  if (error || !item) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <button onClick={() => navigate('/continuous-compliance/risk/poam')} className="text-sm text-muted-foreground hover:text-foreground">POA&amp;M Reports</button>
          <ChevronRight className="size-3.5 text-muted-foreground" />
          <span className="text-sm">Error</span>
        </div>
        <div className="flex flex-col items-center gap-3 py-16 rounded-xl border border-border bg-card">
          <p className="text-sm text-muted-foreground">Something went wrong.</p>
        </div>
      </div>
    );
  }

  const isAutomatedFinding =
    (item.affectedFrameworkControls?.length ?? 0) > 0
    || (item.frameworkControls?.length ?? 0) > 0
    || (item.affectedAssets?.length ?? 0) > 0
    || !!item.techControlStatement;

  return (
    <div className="space-y-4">
      {/* ── HERO HEADER ── breadcrumb + identity chips + title + dates in one compact block ── */}
      <div className="relative rounded-2xl border border-[#e2e8f0] dark:border-white/[0.08] bg-white dark:bg-[#131320]">
        <div className="px-4 py-3 sm:px-5 sm:py-3.5 space-y-2.5">
          {/* Breadcrumb */}
          <div className="flex items-center gap-1.5 text-xs flex-wrap">
            <button
              onClick={() => navigate(`/continuous-compliance/risk/poam/${poamId}`)}
              className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="size-3" /> Back to Board
            </button>
            <ChevronRight className="size-3 text-muted-foreground" />
            <span className="text-muted-foreground">
              {poamTitle}
            </span>
          </div>

          {/* Identity chips row: type + status + control id + severity selector inline */}
          <div className="flex items-center gap-2 flex-wrap">
            {item.itemKey?.trim() && (
              <span
                title={item.itemKey}
                className="inline-flex items-center flex-shrink-0 max-w-[160px] truncate whitespace-nowrap text-[10px] font-bold tracking-wide text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-500/10 px-2 py-0.5 rounded"
              >
                {item.itemKey}
              </span>
            )}
            {isAutomatedFinding ? (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold tracking-wide text-cyan-700 dark:text-cyan-300 bg-cyan-50 dark:bg-cyan-500/10 px-2 py-0.5 rounded">
                <Zap className="size-2.5" /> Automated
              </span>
            ) : (
              <span className="inline-flex items-center text-[10px] font-bold tracking-wide text-violet-700 dark:text-violet-300 bg-violet-50 dark:bg-violet-500/10 px-2 py-0.5 rounded">
                Manual
              </span>
            )}
            <ItemStatusBadge status={boardStatus} />

            {/* Control ID — single flat chip matching the rest of the row */}
            {item.controlId && (
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-500/10 px-2 py-0.5 rounded">
                Control {item.controlId?.toUpperCase()}
              </span>
            )}

            {/* Severity selector — inline with the chip row */}
            <div className="ml-auto">
              <Dropdown
                size="sm"
                variant="pill"
                className="min-w-[130px] w-[130px]"
                value={item.severity ?? ""}
                placeholder="Set severity"
                disabled={savingSeverity}
                onChange={(v) => handleSetSeverity(v as PoamSeverity)}
                options={(["critical", "high", "medium", "low"] as const).map((s) => ({
                  value: s,
                  label: ITEM_SEVERITY_META[s].label,
                  icon: <span className={`size-2 rounded-full ${ITEM_SEVERITY_META[s].dot}`} />,
                }))}
              />
            </div>
          </div>

          {/* Title — full width, no right-side dropdown taking space */}
          <h1 className="text-lg sm:text-xl font-bold text-foreground leading-snug">
            {item.controlTitle || item.title?.replace(/^technical control\b/i, "Control")}
          </h1>

          {/* Meta strip — dates inline */}
          <div className="flex items-center gap-3 text-xs text-[#475569] dark:text-white/70 flex-wrap">
            <span className="flex items-center gap-1.5">
              <Calendar className="size-3" />
              Created {new Date(item.createdAt).toLocaleDateString("en-GB")}
            </span>
            <span className="text-[#cbd5e1] dark:text-white/15">•</span>
            <span className="flex items-center gap-1.5">
              <Calendar className="size-3" />
              Updated {new Date(item.updatedAt).toLocaleDateString("en-GB")}
            </span>
          </div>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg border border-border bg-card text-sm text-muted-foreground">
          <span>Something went wrong.</span>
          <button onClick={fetchData} className="ml-auto text-xs underline hover:no-underline">Retry</button>
        </div>
      )}

      {/* Overview body */}
      <div className="space-y-3">

        {item.description && (
          <div className="rounded-xl border border-[#e2e8f0] dark:border-white/[0.06] bg-white dark:bg-[#131320] px-4 py-3 flex items-start gap-2.5">
            <div className="size-7 rounded-md bg-indigo-50 dark:bg-indigo-500/10 flex items-center justify-center flex-shrink-0">
              <FileText className="size-3.5 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-bold tracking-wide text-[#475569] dark:text-white/75 mb-1">
                {(item.affectedFrameworkControls?.length ?? 0) > 0 || (item.affectedAssets?.length ?? 0) > 0
                  ? "Summary"
                  : item.statementId
                    ? "Justification"
                    : "Description"}
              </p>
              <p className="text-sm text-foreground dark:text-white/90 leading-relaxed whitespace-pre-wrap break-words">
                {item.description?.replace(/^technical control\b/i, "Control")}
              </p>
            </div>
          </div>
        )}

        {(() => {
          const isAutomated = (item.affectedFrameworkControls?.length ?? 0) > 0
            || (item.frameworkControls?.length ?? 0) > 0
            || (item.affectedAssets?.length ?? 0) > 0
            || !!item.techControlStatement;
          // Prefer rich frameworkControls; fall back to plain id chips.
          const fwControls: { control_id: string; statement?: string | null }[] =
            (item.frameworkControls?.length ?? 0) > 0
              ? item.frameworkControls!
              : (item.affectedFrameworkControls ?? []).map((id) => ({ control_id: id }));
          const assets = item.affectedAssets ?? [];
          const assetUuidsOnly = (assets.length === 0 ? (item.affectedAssetUuids ?? []) : []);
          const show = isAutomated || !!item.statementId;
          if (!show) return null;

          return (
            <div className="rounded-xl border border-[#e2e8f0] dark:border-white/[0.06] bg-white dark:bg-[#131320] p-4 space-y-3.5">
              {/* Control Statement */}
              {item.techControlStatement && (
                <div className="min-w-0">
                  <p className="text-xs font-bold tracking-wide text-[#475569] dark:text-white/75 mb-1.5">
                    Control Statement
                  </p>
                  <p className="text-sm text-foreground dark:text-white/90 leading-relaxed whitespace-pre-wrap break-words">
                    {item.techControlStatement}
                  </p>
                </div>
              )}

              {/* Affected Controls — full-width row, wraps freely for any quantity */}
              {fwControls.length > 0 && (
                <div className="pt-0.5">
                  <div className="flex items-baseline gap-2 mb-1.5">
                    <p className="text-xs font-bold tracking-wide text-[#475569] dark:text-white/75">
                      Affected Controls
                    </p>
                    <span className="text-[11px] font-medium text-[#64748b] dark:text-white/55 tabular-nums">
                      ({fwControls.length})
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {fwControls.map((fc) =>
                      fc.statement ? (
                        <TooltipProvider key={fc.control_id} delayDuration={150}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="inline-flex items-center text-[11.5px] font-mono font-semibold text-indigo-600 dark:text-violet-400 bg-indigo-50 dark:bg-violet-500/10 px-2 py-0.5 rounded cursor-default">
                                {fc.control_id?.toUpperCase()}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="bottom" align="start" className="max-w-xs p-2.5 space-y-1.5">
                              <p className="text-[10px] font-bold tracking-[0.08em] text-indigo-300 dark:text-indigo-600">
                                Control Statement
                              </p>
                              <p className="text-xs leading-relaxed">{fc.statement}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ) : (
                        <span key={fc.control_id} className="inline-flex items-center text-[11.5px] font-mono font-semibold text-indigo-600 dark:text-violet-400 bg-indigo-50 dark:bg-violet-500/10 px-2 py-0.5 rounded">
                          {fc.control_id?.toUpperCase()}
                        </span>
                      ),
                    )}
                  </div>
                </div>
              )}

              {assets.length > 0 && (
                <div className="pt-1 border-t border-[#f1f5f9] dark:border-white/[0.05]">
                  <p className="text-xs font-bold tracking-wide text-[#475569] dark:text-white/75 mt-2.5 mb-1.5">
                    Affected Assets ({assets.length})
                  </p>
                  <div className="rounded-lg border border-[#e2e8f0] dark:border-white/[0.06] overflow-hidden divide-y divide-[#e2e8f0] dark:divide-white/[0.06]">
                    {assets.map((asset) => {
                      const expanded = expandedAssets.has(asset.asset_uuid);
                      const hasDetail = !!asset.command || !!asset.evidence_data;
                      return (
                        <div key={asset.asset_uuid}>
                          <button
                            type="button"
                            onClick={() => hasDetail && toggleAsset(asset.asset_uuid)}
                            className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 text-left ${hasDetail ? "hover:bg-[#f8fafc] dark:hover:bg-white/[0.02] cursor-pointer" : "cursor-default"}`}
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              {hasDetail && (
                                <ChevronRight className={`size-3.5 text-[#94a3b8] flex-shrink-0 transition-transform ${expanded ? "rotate-90" : ""}`} />
                              )}
                              <div className="min-w-0">
                                <p className="text-sm font-semibold text-foreground dark:text-white/95 truncate">
                                  {asset.hostname || asset.asset_uuid}
                                </p>
                                {(asset.component_name || asset.scanned_at) && (
                                  <p className="text-xs text-[#475569] dark:text-white/70 truncate">
                                    {asset.component_name}
                                    {asset.component_name && asset.scanned_at && " · "}
                                    {asset.scanned_at && `scanned ${new Date(asset.scanned_at).toLocaleString("en-GB")}`}
                                  </p>
                                )}
                              </div>
                            </div>
                          </button>
                          {expanded && hasDetail && (
                            <div className="px-3 pb-3 space-y-2 bg-[#f8fafc] dark:bg-white/[0.02]">
                              {asset.command && (
                                <div>
                                  <p className="text-[11px] font-bold tracking-wide text-[#64748b] dark:text-white/50 mb-1">Command Executed</p>
                                  <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-words rounded-md border border-[#e2e8f0] dark:border-white/[0.06] bg-white dark:bg-[#0f111a] p-2.5 text-[11px] font-mono leading-relaxed text-[#334155] dark:text-white/70">
                                    {asset.command}
                                  </pre>
                                </div>
                              )}
                              {asset.evidence_data && (
                                <div>
                                  <p className="text-[11px] font-bold tracking-wide text-[#64748b] dark:text-white/50 mb-1">Evidence</p>
                                  <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-words rounded-md border border-[#e2e8f0] dark:border-white/[0.06] bg-white dark:bg-[#0f111a] p-2.5 text-[11px] font-mono leading-relaxed text-[#334155] dark:text-white/70">
                                    {asset.evidence_data}
                                  </pre>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {assetUuidsOnly.length > 0 && (
                <div>
                  <p className="text-xs font-bold tracking-wide text-[#475569] dark:text-white/75 mb-1.5">
                    Affected Assets ({assetUuidsOnly.length})
                  </p>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {assetUuidsOnly.map((uuid) => (
                      <span key={uuid} className="inline-flex items-center text-[11px] font-mono text-[#475569] dark:text-white/60 bg-[#f1f5f9] dark:bg-white/[0.04] px-2 py-0.5 rounded">
                        {uuid}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {item.statementId && (
                <div>
                  <p className="text-xs font-bold tracking-wide text-[#475569] dark:text-white/75 mb-1.5">
                    Statement
                  </p>
                  <span className="inline-flex items-center text-[12px] font-mono font-semibold text-indigo-600 dark:text-violet-400 bg-indigo-50 dark:bg-violet-500/10 px-2 py-0.5 rounded">
                    {item.statementId?.toUpperCase()}
                  </span>
                  {item.statementDescription && (
                    <p className="text-sm text-foreground/90 dark:text-white/85 leading-relaxed mt-1.5">
                      {item.statementDescription}
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })()}
      </div>

      {/* ── REMEDIATION PLAN + MILESTONES — side-by-side on lg, stacked on smaller ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 items-start">
        {/* Remediation Plan - OPEN: Editable */}
        {boardStatus === 'open' && (
          <div className="rounded-xl border border-[#e2e8f0] dark:border-white/[0.06] bg-white dark:bg-[#131320] p-4 space-y-3">
            <div>
              <h3 className="text-sm font-bold text-foreground dark:text-white/95">Remediation Plan</h3>
              <p className="text-xs text-[#475569] dark:text-white/70 mt-0.5">Define your plan before starting remediation.</p>
            </div>
            <div className="space-y-3">
              <FormField label="Plan Title" required>
                <FormInput placeholder="Enter remediation plan title" value={planTitle} onChange={handlePlanFieldChange(setPlanTitle)} disabled={saving} />
              </FormField>
              <FormField label="Description" required>
                <FormTextarea placeholder="Describe the remediation approach" value={planDesc} onChange={handlePlanFieldChange(setPlanDesc)} rows={2} />
              </FormField>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <FormField label="Assignee" required>
                  <FormSelect
                    placeholder="Select assignee"
                    value={assignee}
                    onChange={handlePlanFieldChange(setAssignee)}
                    options={users.map((u) => ({
                      value: u.displayName || `${u.givenName} ${u.familyName}`.trim() || u.email,
                      label: `${u.displayName || `${u.givenName} ${u.familyName}`.trim() || u.email}${u.email ? ` (${u.email})` : ''}`,
                    }))}
                  />
                </FormField>
                <FormField label="Scheduled Completion" required>

                  <FormDatePicker
                    value={planDate}
                    onChange={handlePlanFieldChange(setPlanDate)}
                    min={todayISO()}
                  />
                </FormField>
              </div>
            </div>
            {showSaveButton && (
              <div className="flex justify-end pt-2 border-t border-[#f1f5f9] dark:border-white/[0.04]">
                <button
                  onClick={handleSavePlan}
                  disabled={!planTitle.trim() || !planDesc.trim() || !planDate || !assignee.trim() || saving}
                  className="px-4 py-2 rounded-lg bg-indigo-600 dark:bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors disabled:opacity-40 shadow-md shadow-indigo-500/20 dark:shadow-indigo-500/20"
                >
                  {saving ? 'Saving' : 'Save Plan'}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Remediation Plan - Non-open: Read-only */}
        {boardStatus !== 'open' && hasPlan && (
          <div className="rounded-xl border border-[#e2e8f0] dark:border-white/[0.06] bg-white dark:bg-[#131320] p-4 space-y-3">
            <div>
              <h3 className="text-sm font-bold text-foreground dark:text-white/95">Remediation Plan</h3>
              {boardStatus === 'completed' && (
                <p className="text-[11px] text-emerald-600 dark:text-emerald-400 mt-0.5 font-medium">
                  Completed{actualCompletionDate ? ` on ${actualCompletionDate}` : ''}
                </p>
              )}
              {boardStatus === 'in_progress' && (
                <p className="text-xs text-[#475569] dark:text-white/70 mt-0.5">In progress — plan locked</p>
              )}
              {boardStatus === 'progress_hold' && (
                <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-0.5 font-medium">On hold — plan locked</p>
              )}
              {boardStatus === 'verification' && (
                <p className="text-[11px] text-blue-600 dark:text-blue-400 mt-0.5 font-medium">Under verification — plan locked</p>
              )}
            </div>
            <div className="rounded-lg bg-[#f5f7fb] dark:bg-white/[0.03] border border-[#e2e8f0] dark:border-white/[0.05] p-3 space-y-2.5">
              <div>
                <p className="text-xs font-bold tracking-wide text-[#475569] dark:text-white/75 mb-0.5">Title</p>
                <p className="text-sm font-medium text-foreground dark:text-white/95">{planTitle}</p>
              </div>
              <div>
                <p className="text-xs font-bold tracking-wide text-[#475569] dark:text-white/75 mb-0.5">Description</p>
                <p className="text-sm text-foreground/90 dark:text-white/85 leading-relaxed">{planDesc}</p>
              </div>
              <div className="grid grid-cols-2 gap-3 pt-1 border-t border-[#e2e8f0]/60 dark:border-white/[0.04]">
                {assignee && (
                  <div>
                    <p className="text-xs font-bold tracking-wide text-[#475569] dark:text-white/75 mb-1">Assignee</p>
                    <div className="flex items-center gap-1.5">
                      <div className="size-5 rounded-full bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center text-[10px] font-bold text-white ">
                        {assignee.charAt(0)}
                      </div>
                      <p className="text-sm text-foreground truncate">{assignee}</p>
                    </div>
                  </div>
                )}
                <div>
                  <p className="text-xs font-bold tracking-wide text-[#475569] dark:text-white/75 mb-1">Scheduled</p>
                  <p className="text-sm text-foreground">{isoToDisplay(planDate) || planDate}</p>
                </div>
                {actualCompletionDate && (
                  <div>
                    <p className="text-xs font-bold tracking-wide text-[#475569] dark:text-white/75 mb-1">Actual</p>
                    <p className="text-sm text-emerald-600 dark:text-emerald-400 font-medium">{actualCompletionDate}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Milestones */}
        <div className="rounded-xl border border-[#e2e8f0] dark:border-white/[0.06] bg-white dark:bg-[#131320] p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-bold text-foreground dark:text-white/95">Milestones</h3>
              {totalMilestones > 0 && (
                <p className="text-xs text-[#475569] dark:text-white/70 mt-0.5 tabular-nums">{completedMilestones} of {totalMilestones} completed</p>
              )}
            </div>
            {boardStatus === 'open' && (
              <RBACAction module="poam" action="create">
                <button
                  onClick={handleOpenAddMilestone}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-[#e2e8f0] dark:border-white/[0.07] text-[11px] font-medium text-[#475569] dark:text-white/60 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] transition-colors"
                >
                  <Plus className="size-3" />
                  Add
                </button>
              </RBACAction>
            )}
          </div>

          {/* Progress bar */}
          {totalMilestones > 0 && (
            <div className="h-1.5 rounded-full bg-[#f1f5f9] dark:bg-white/[0.05] overflow-hidden">
              <div
                className="h-full rounded-full bg-emerald-500 transition-all duration-500"
                style={{ width: `${(completedMilestones / totalMilestones) * 100}%` }}
              />
            </div>
          )}

          {/* Milestone list */}
          {totalMilestones === 0 ? (
            <div className="flex flex-col items-center gap-1 py-6 rounded-lg bg-[#f8fafc]/50 dark:bg-white/[0.015]">
              <p className="text-sm font-medium text-foreground/85 dark:text-white/80">No milestones yet</p>
              {boardStatus === 'open' && (
                <p className="text-xs text-[#475569] dark:text-white/60 text-center px-4">
                  {hasPlan
                    ? 'Add milestones to track progress'
                    : 'Fill in the remediation plan first, then add milestones'}
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {milestones.map(m => (
                <div
                  key={m.id ?? m.name}
                  className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 transition-colors ${
                    boardStatus === 'in_progress' ? 'hover:border-[#c7d2e8] dark:hover:border-white/[0.12] cursor-pointer' : ''
                  } border-[#e2e8f0] dark:border-white/[0.06] bg-[#fafafa] dark:bg-white/[0.02]`}
                  onClick={() => boardStatus === 'in_progress' && handleToggleMilestone(m.id ?? m.name)}
                >
                  <div className="flex-1 min-w-0 space-y-0.5">
                    <p className={`text-sm font-medium ${m.completed ? 'line-through text-foreground/60 dark:text-white/45' : 'text-foreground dark:text-white/95'}`}>
                      {m.name}
                    </p>
                    {m.due && (
                      <div className="flex items-center gap-1 text-xs text-[#475569] dark:text-white/70">
                        <Calendar className="size-3" />
                        {m.due}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {boardStatus === 'in_progress' && (
                      <button
                        type="button"
                        onClick={e => { e.stopPropagation(); handleToggleMilestone(m.id ?? m.name); }}
                        className={`size-5 rounded-full border-2 flex items-center justify-center transition-all focus:outline-none cursor-pointer ${
                          m.completed
                            ? 'border-emerald-500 bg-emerald-500 dark:border-emerald-400 dark:bg-emerald-500'
                            : 'border-[#cbd5e1] dark:border-white/[0.2] bg-white dark:bg-transparent hover:border-emerald-400'
                        }`}
                      >
                        {m.completed && (
                          <svg className="size-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </button>
                    )}
                    {boardStatus === 'open' && (
                      <>
                        <RBACAction module="poam" action="update">
                          <Tip label="Edit Milestone" side="bottom">
                            <button
                              onClick={e => { e.stopPropagation(); setEditMsId(m.id ?? m.name); setEditMsTitle(m.name); setShowEditMs(true); }}
                              className="p-1 rounded-md text-[#94a3b8] dark:text-white/30 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 transition-colors"
                            >
                              <Pencil className="size-3.5" />
                            </button>
                          </Tip>
                        </RBACAction>
                        <RBACAction module="poam" action="delete">
                          <Tip label="Delete Milestone" side="bottom">
                            <button
                              onClick={e => { e.stopPropagation(); handleDeleteMilestone(m.id ?? m.name); }}
                              className="p-1 rounded-md text-[#94a3b8] dark:text-white/30 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
                            >
                              <Trash2 className="size-3.5" />
                            </button>
                          </Tip>
                        </RBACAction>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* All milestones done banner */}
          {boardStatus === 'in_progress' && allMilestonesComplete && (
            <div className="px-2.5 py-2 rounded-lg bg-emerald-50 dark:bg-emerald-500/[0.08] border border-emerald-200 dark:border-emerald-500/20">
              <p className="text-[11.5px] text-emerald-700 dark:text-emerald-400 font-medium">
                All milestones complete  ready for Validation & Testing
              </p>
            </div>
          )}

          {/* Move to Completed */}
          {boardStatus === 'in_progress' && allMilestonesComplete && (
            <div className="flex justify-end pt-2 border-t border-[#f1f5f9] dark:border-white/[0.04]">
              <RBACAction module="poam" action="update">
                <button
                  onClick={() => setShowCompleteDialog(true)}
                  disabled={saving}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold transition-colors disabled:opacity-40 shadow-md shadow-emerald-500/20"
                >
                  <CheckCheck className="size-3.5" />
                  Move to Validation
                </button>
              </RBACAction>
            </div>
          )}
        </div>
      </div>

      {/* ── Edit Milestone Dialog ── */}
      <Modal
        open={showEditMs}
        onClose={() => { setShowEditMs(false); setEditMsId(''); setEditMsTitle(''); }}
        title="Edit Milestone"
        description="Update the milestone title"
        footer={
          <ModalFooterButtons
            onCancel={() => { setShowEditMs(false); setEditMsId(''); setEditMsTitle(''); }}
            onSubmit={handleEditMilestone}
            submitLabel="Save Changes"
            submitDisabled={!editMsTitle.trim() || saving}
          />
        }
      >
        <FormField label="Title" required>
          <FormInput placeholder="e.g. Deploy Vault in production" value={editMsTitle} onChange={setEditMsTitle} />
        </FormField>
      </Modal>

      {/* ── Add Milestone Dialog ── */}
      <Modal
        open={showAddMs}
        onClose={() => { setShowAddMs(false); setMsTitle(''); }}
        title="Add Milestone"
        description="Add a new milestone to track remediation progress"
        footer={
          <ModalFooterButtons
            onCancel={() => { setShowAddMs(false); setMsTitle(''); }}
            onSubmit={handleAddMilestone}
            submitLabel="Add Milestone"
            submitDisabled={!msTitle.trim()}
          />
        }
      >
        <FormField label="Title" required>
          <FormInput placeholder="e.g. Deploy Vault in production" value={msTitle} onChange={setMsTitle} />
        </FormField>
      </Modal>

      {/* ── Start Remediation Confirmation ── */}
      <Modal
        open={showStartDialog}
        onClose={() => setShowStartDialog(false)}
        title="Start Remediation?"
        description="This will lock the remediation plan and move the item to In Progress."
        footer={
          <ModalFooterButtons
            onCancel={() => setShowStartDialog(false)}
            onSubmit={handleStartRemediation}
            submitLabel="Start Remediation"
          />
        }
      >
        <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-500/[0.08] border border-amber-200 dark:border-amber-500/20 text-xs text-amber-700 dark:text-amber-400 leading-relaxed">
          Once started, the remediation plan and milestone list will become <strong>read-only</strong>. Make sure all details are correct before proceeding.
        </div>
      </Modal>

      {/* ── Move to Validation & Testing Confirmation ── */}
      <Modal
        open={showCompleteDialog}
        onClose={() => setShowCompleteDialog(false)}
        title="Move to Validation & Testing?"
        description="This will move the item to the Validation & Testing stage for review."
        footer={
          <>
            <button
              onClick={() => setShowCompleteDialog(false)}
              className="px-4 py-2 rounded-lg border border-[#e2e8f0] dark:border-white/[0.07] text-sm font-medium text-[#475569] dark:text-white/60 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleMoveToCompleted}
              disabled={saving}
              className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold transition-colors disabled:opacity-40 shadow-md shadow-emerald-500/20"
            >
              Move to Validation & Testing
            </button>
          </>
        }
      >
        <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-500/[0.08] border border-blue-200 dark:border-blue-500/20 text-xs text-blue-700 dark:text-blue-400 leading-relaxed">
          All {totalMilestones} milestones are complete. Confirming will move this item to <strong>Validation &amp; Testing</strong> for review.
        </div>
      </Modal>
    </div>
  );
}
